/** Iter 639 — vendor panel split · live 8-item KYC checklist + per-doc review status + bank account row
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, CheckCircle2, FileText } from "lucide-react";
import { sharedApi } from "@/lib/api";
import {
  ENGAGEMENT_HEADERS, ENGAGEMENT_PARAM, KYC_CHECKLIST_LABELS,
  REQUIRED_KYC_DOCS, errMsg,
} from "@/features/vendorPanel/helpers";

export default function KycProgressPanel({ vendor }) {
  const qc = useQueryClient();
  const checklist = vendor.kyc_checklist || {};
  const docs = vendor.kyc_documents || [];
  const items = Object.keys(KYC_CHECKLIST_LABELS);
  const doneCount = items.filter((k) => checklist[k]).length;

  return (
    <div
      className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/60 p-5"
      data-testid="partner-kyc-progress"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          Verification checklist
        </div>
        <div className="text-xs font-bold text-slate-600" data-testid="partner-kyc-progress-count">
          {doneCount}/{items.length} complete
        </div>
      </div>

      <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {items.map((k) => (
          <li
            key={k}
            data-testid={`partner-kyc-check-${k}`}
            className="flex items-center gap-2 text-sm"
          >
            {checklist[k] ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <span className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" aria-hidden="true" />
            )}
            <span className={checklist[k] ? "text-slate-700" : "text-slate-500"}>
              {KYC_CHECKLIST_LABELS[k]}
            </span>
          </li>
        ))}
      </ul>

      {docs.length > 0 && (
        <div className="mt-4" data-testid="partner-kyc-doc-list">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Your documents
          </div>
          <ul className="mt-2 space-y-1.5">
            {docs.map((d) => (
              <li
                key={d.id}
                data-testid={`partner-kyc-doc-${d.id}`}
                className="flex items-center gap-2 rounded-xl bg-white border border-soft px-3 py-2 text-sm"
              >
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-slate-700">
                  {REQUIRED_KYC_DOCS.find((r) => r.type === d.doc_type)?.label ||
                    KYC_CHECKLIST_LABELS[d.doc_type] ||
                    (d.doc_type || "Document").replace(/_/g, " ")}
                  {d.file_name ? ` — ${d.file_name}` : ""}
                </span>
                <DocStatusPill status={d.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {!checklist.bank_account_or_connect && (
        <BankAccountRow
          current={vendor.bank_account || ""}
          onSaved={() => qc.invalidateQueries({ queryKey: ["partner-vendor-me"] })}
        />
      )}
    </div>
  );
}

function DocStatusPill({ status }) {
  if (status === "approved") {
    return (
      <span className="shrink-0 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
        Approved
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="shrink-0 rounded-full bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
        Rejected
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
      In review
    </span>
  );
}

/* Iter-316 addendum (b) — bank account via PUT /vendors/me. Saving flips
   the `bank_account_or_connect` checklist item without needing Stripe. */
function BankAccountRow({ current, onSaved }) {
  const [val, setVal] = useState(current);
  const saveMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.put(
        "/classifieds/engagement/vendors/me",
        { bank_account: val.trim() },
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Bank account saved.");
      onSaved();
    },
    onError: (err) => toast.error(errMsg(err, "Couldn't save bank account")),
  });

  return (
    <div className="mt-4" data-testid="partner-bank-account-row">
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        Bank account for payouts
      </div>
      <p className="mt-1 text-xs text-slate-500">
        No Stripe? Add your NZ bank account instead — either one completes the payout step.
      </p>
      <form
        className="mt-2 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (val.trim()) saveMut.mutate();
        }}
      >
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="12-3456-7890123-00"
          inputMode="numeric"
          data-testid="partner-bank-account-input"
          className="flex-1 rounded-full bg-white border border-slate-300 focus:border-[color:var(--brand-blue)] px-4 py-2 text-sm focus:outline-none"
        />
        <button
          type="submit"
          disabled={!val.trim() || saveMut.isPending}
          data-testid="partner-bank-account-save"
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-2 disabled:opacity-60 transition-colors"
        >
          {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </button>
      </form>
    </div>
  );
}
